<template>
  <section>
    <!-- Si tengo id de materia, puedo editar su seriación -->
    <div v-if="materia">
      <!-- Nueva seriación -->
      <div>
        <b-row>
          <!-- _Materia de requerimiento -->
          <b-col
            md="12"
            class="mb-1"
          >
            <b-form-group
              label="Materia"
              label-for="materia-seriacion"
              class="mb-2"
            >
              <v-select
                id="materia-seriacion"
                v-model="seriacion.materia_req"
                placeholder="Seleccione materia"
                label="nombre"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="totalMaterias"
                :reduce="req => req.id"
                :selectable="req => (!seriacionesProp.find(item => item.materia_req === req.id) && req.id !== materia)"
                :clearable="false"
              >
                <!-- Como se ve cuando no hay opciones -->
                <template #no-options>
                  <em style="opacity: 0.5;">No hay resultados para mostar</em>
                </template>
              </v-select>
            </b-form-group>
          </b-col>
          <!-- Tipo de requerimiento -->
          <b-col
            md="12"
            class="mb-1"
          >
            <b-form-group
              label="Tipo de requerimiento"
              label-for="tipo-seriacion"
              class="mb-2"
            >
              <v-select
                id="tipo-seriacion"
                v-model="seriacion.tipo"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                placeholder="Seleccione tipo"
                label="key"
                :options="seriacionOpciones"
                :clearable="false"
                :reduce="ser => ser.key"
              >
                <!-- Como se ven las opciones -->
                <template #option="{ key }">
                  <span>
                    <b>{{ key }}</b> - {{ seriacionTipo(key) }}
                  </span>
                </template>
                <!-- Como se ve la opción seleccionada -->
                <template #selected-option="{ key }">
                  <div style="display: flex; align-items: baseline;">
                    <b>{{ key }}</b><em style="margin-left: .5rem;"> - {{ seriacionTipo(key) }}</em>
                  </div>
                </template>
              </v-select>
            </b-form-group>
          </b-col>
        </b-row>
        <!-- Crear nueva seriación -->
        <div class="text-center mt-1">
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
            @click="addSeriacion()"
          >
            <feather-icon
              icon="PlusIcon"
              class="mr-25"
            />
            <span>Nueva seriación</span>
          </b-button>
        </div>
      </div>

      <hr>

      <b-card-header>
        <b-card-title>Materias registradas como requerimiento</b-card-title>
      </b-card-header>

      <!-- body -->
      <b-card-body>
        <div
          v-for="item in seriacionesProp"
          :id="item.id"
          :key="item.id"
          class="d-flex justify-content-between align-items-center mb-2"
        >
          <b-media no-body>
            <b-media-aside class="mr-75">
              <b-avatar
                rounded
                variant="light-primary"
              >
                <feather-icon
                  icon="AlertCircleIcon"
                  size="20"
                />
              </b-avatar>
            </b-media-aside>
            <b-media-body class="my-auto">
              <h6 class="mb-0">
                {{ item.nombre }}
              </h6>
              <small>{{ item.tipo }} - {{ seriacionTipo(item.tipo) }}</small>
            </b-media-body>
          </b-media>
          <div class="d-flex align-items-center">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="danger"
              size="sm"
              @click="deleteSeriacion(item)"
            >
              <feather-icon
                icon="Trash2Icon"
                class="mr-50"
              />
              <span class="align-middle">Eliminar</span>
            </b-button>
          </div>
        </div>
        <div
          v-if="seriacionesProp.length > 1"
          class="text-center mt-1"
        >
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="danger"
            @click="deleteSeriacion(null, true)"
          >
            <feather-icon
              icon="XIcon"
              class="mr-25"
            />
            <span>Borrar todas las materias</span>
          </b-button>
        </div>
      </b-card-body>
    </div>
    <!-- Si no tengo id de materia, muestro mensaje -->
    <div v-else>
      <b-alert
        variant="danger"
        show
      >
        <div class="alert-body">
          Para poder modificar la seriación de la materia, <b>es necesario haberla dado de alta</b>, para ello guarde la información de la pestaña <b>"Principal"</b>, posteriormente, búsque la asignatura en el listado general y <b>modifique su seriación de ser necesario</b>
        </div>
      </b-alert>
    </div>
  </section>
</template>

<script>
import {
  BRow,
  BCol,
  BButton,
  BAlert,
  BCardHeader,
  BCardTitle,
  BCardBody,
  BMedia,
  BAvatar,
  BMediaAside,
  BMediaBody,
  BFormGroup,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import store from '@/store'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { ref, onMounted } from '@vue/composition-api'
import vSelect from 'vue-select'
import { seriacionTipo } from '@core/utils/filter'
import { materiasList } from '../useMaterias'

export default {
  components: {
    BRow,
    BCol,
    BButton,
    BAlert,
    vSelect,
    BCardHeader,
    BCardTitle,
    BCardBody,
    BMedia,
    BAvatar,
    BMediaAside,
    BMediaBody,
    BFormGroup,
  },
  directives: {
    Ripple,
  },
  props: {
    materia: {
      type: Number,
      default: null,
    },
    seriacionesProp: {
      type: Array,
      default: () => [],
    },
  },
  setup(props, { emit }) {
    const toast = useToast()
    const totalMaterias = ref([])
    const seriacionOpciones = [
      { key: 'MP' },
      { key: 'AP' },
    ]
    const seriacion = ref({ materia_dep: props.materia, materia_req: null, tipo: 'AP' })

    // Load asignature's data
    const loadAsignatures = () => {
      // loader.value.form = true
      materiasList().then(response => {
        // loader.value.form = false
        totalMaterias.value = response
      }, error => {
        // loader.value.form = false
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error en petición [Información de materia]',
            text: error,
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        },
        { timeout: 10000 })
      })
    }

    // Create new seriacion
    const addSeriacion = () => {
      const data = seriacion.value
      data.action = 9
      data.task = 'wsmaterias'
      emit('set-loader', true)

      store.dispatch('servicioEscolar/postServicioEscolar', data)
        .then(response => {
          emit('set-loader', false)
          toast({
            component: ToastificationContent,
            props: {
              title: 'Solicitud exitosa',
              text: response.message,
              icon: 'CheckIcon',
              variant: 'success',
            },
          },
          { timeout: 8000 })
          seriacion.value = { materia_dep: props.materia, materia_req: null, tipo: 'AP' }
          emit('reload-data')
        }, error => {
          emit('set-loader', false)
          toast({
            component: ToastificationContent,
            props: {
              title: 'Error en petición [CRUD seriación]',
              text: error,
              icon: 'AlertCircleIcon',
              variant: 'danger',
            },
          },
          { timeout: 10000 })
        })
    }

    // Delete one or all asignatures
    const deleteSeriacion = (materia, deleteAll = false) => {
      const data = (!deleteAll) ? { action: 10, seriacion: materia.id } : { action: 9, materia_dep: props.materia }
      data.task = 'wsmaterias'
      emit('set-loader', true)

      store.dispatch('servicioEscolar/postServicioEscolar', data)
        .then(response => {
          emit('set-loader', false)
          toast({
            component: ToastificationContent,
            props: {
              title: 'Solicitud exitosa',
              text: response.message,
              icon: 'CheckIcon',
              variant: 'success',
            },
          },
          { timeout: 8000 })
          emit('reload-data')
        }, error => {
          emit('set-loader', false)
          toast({
            component: ToastificationContent,
            props: {
              title: 'Error en petición [CRUD Módulos]',
              text: error,
              icon: 'AlertCircleIcon',
              variant: 'danger',
            },
          },
          { timeout: 10000 })
        })
    }

    onMounted(
      () => {
        loadAsignatures()
      },
    )

    return {
      totalMaterias,
      seriacion,
      seriacionTipo,
      addSeriacion,
      deleteSeriacion,
      seriacionOpciones,
    }
  },
}
</script>

<style lang="scss" scoped>
.repeater-form {
  overflow: hidden;
  transition: .35s height;
}
</style>
